import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Subject } from "rxjs/internal/Subject";
import { takeUntil } from "rxjs/operators";
import { interval } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthService } from "./services/auth.service";
import { UserRepositoryService } from "./services/user-repository.service";
import { DowntimeRepositoryService } from "./services/downtime-repository.service";
import { User } from "./shared/models/user.model";
import { AppConstants } from "./app.constants";
import { filter } from "rxjs/operators";
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  title = "";
  isLoggedIn = false;
  user: User = null;
  showLoader = false;
  showHeader: boolean = false;
  showHeaderFooter: boolean = false;
  private unsubscribe: Subject<void> = new Subject<void>();
  private readonly _destroying$ = new Subject<void>();
  loginDisplay = false;
  displayBlankPage: boolean = false;
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private msalService: MsalService,
    private userRepository: UserRepositoryService,
    private authService: AuthService,
    private renderer: Renderer2,
    private downtimeService: DowntimeRepositoryService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => this.modifyHeader());
  }
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  ngOnInit() {
    this.msalBroadcastService.msalSubject$.subscribe({
      next: (msalSubject) => {
        console.log(msalSubject.eventType);
        if (msalSubject.eventType === 'msal:acquireTokenFailure') {
          // Don't logout if we're on the maintenance page (downtime active)
          if (this.router.url !== '/Maintenance') {
            this.msalService.logout();
          }
        }
      }
    });

    // Poll for active downtime every 60 seconds
    this.startDowntimePolling();
  }

  private startDowntimePolling(): void {
    interval(60000)
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        if (this.router.url !== '/Maintenance' && this.router.url !== '/Unauthorized') {
          this.checkDowntimeStatus();
        }
      });
  }

  private checkDowntimeStatus(): void {
    this.downtimeService.getActive()
      .pipe(takeUntil(this._destroying$))
      .subscribe(
        (activeDowntime) => {
          if (activeDowntime) {
            // Check if user is admin - if not, redirect to maintenance
            this.userRepository.loggedUserData$
              .pipe(takeUntil(this._destroying$))
              .subscribe((userData: any) => {
                if (userData && userData.roleId !== 1 && userData.roleId !== 3) {
                  this.router.navigate(['/Maintenance']);
                }
              });
          }
        },
        () => { /* silently ignore polling errors */ }
      );
  }
 
 

  @HostListener("window:scroll", ["$event"])
  onWindowScroll(e): void {
    if (environment.envt === "PRODUCTION") {
      return;
    }
    const headerWarning = document.getElementById("sg-environment-warning");
    const drawerOpen = document.getElementById("sg-drawer-open");
    if (headerWarning !== null) {
      if (window.pageYOffset > headerWarning.offsetTop) {
        headerWarning.classList.add("sg-sticky-banner");
      } else {
        headerWarning.classList.remove("sg-sticky-banner");
      }
    }
    if (drawerOpen !== null) {
      if (window.pageYOffset > headerWarning.offsetTop) {
        drawerOpen.classList.add("sg-drawer");
      } else {
        drawerOpen.classList.remove("sg-drawer");
      }
    }
  }

  private modifyHeader() {
    this.displayBlankPage = true;
    let urlPath = "/solarForms/accountCreationForm" + window.location.search;
    if (
      this.router.url === "/Unauthorized" ||
      this.router.url === "/signin" ||
      this.router.url === "/Maintenance" ||
      this.router.url === urlPath
    ) {
      
      this.showHeaderFooter = false;
    } else {
      
      this.showHeaderFooter = true;
    }
    this.userRepository.loggedUserData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        if (res) {
          this.displayBlankPage = false;
          this.showHeader = true;
          this.applyThemePreference();
        }
      });
  }

  private applyThemePreference(): void {
    let selectedTheme = AppConstants.defaultTheme;

    try {
      const savedTheme = window.localStorage.getItem(AppConstants.themeStorageKey);
      if (savedTheme === AppConstants.lightTheme || savedTheme === AppConstants.darkTheme) {
        selectedTheme = savedTheme;
      }
    } catch {
      selectedTheme = AppConstants.defaultTheme;
    }

    this.renderer.removeClass(document.body, AppConstants.lightTheme);
    this.renderer.removeClass(document.body, AppConstants.darkTheme);
    this.renderer.addClass(document.body, selectedTheme);
  }
}
